import * as Sentry from '@sentry/vue'

const reportedErrors = new WeakSet()
let lastSyncedLoginInformation
let lastReportedInvalidLoginInformation = null

function isSentryEnabled() {
    return process.env.NODE_ENV === 'production'
}

function normalizeError(error, fallbackMessage) {
    if (error instanceof Error) {
        return error
    }
    if (error != null && typeof error.message === 'string' && error.message !== '') {
        return new Error(error.message)
    }
    if (typeof error === 'string' && error !== '') {
        return new Error(error)
    }
    return new Error(fallbackMessage || 'Handled frontend error')
}

function setScopeTag(scope, name, value) {
    if (value == null || value === '') {
        return
    }
    scope.setTag(name, String(value))
}

export function reportHandledError(error, details) {
    if (!isSentryEnabled()) {
        return
    }

    const normalizedError = normalizeError(error, details && details.message)
    if (reportedErrors.has(normalizedError)) {
        return
    }
    reportedErrors.add(normalizedError)

    const reportDetails = details || {}
    Sentry.withScope(scope => {
        scope.setLevel(reportDetails.level || 'warning')
        scope.setTag('handled_error', 'true')
        setScopeTag(scope, 'module', reportDetails.module)
        setScopeTag(scope, 'operation', reportDetails.operation)

        const tags = reportDetails.tags || {}
        Object.keys(tags).forEach(name => setScopeTag(scope, name, tags[name]))

        if (reportDetails.context != null) {
            scope.setContext('handled_error', reportDetails.context)
        }
        Sentry.captureException(normalizedError)
    })
}

export function syncSentryUserFromStorage() {
    if (!isSentryEnabled()) {
        return
    }

    const storedLoginInformation = localStorage.getItem('loginInformation')
    if (storedLoginInformation === lastSyncedLoginInformation) {
        return
    }
    lastSyncedLoginInformation = storedLoginInformation

    if (storedLoginInformation == null || storedLoginInformation === '') {
        Sentry.setUser(null)
        return
    }

    let loginResponse
    try {
        loginResponse = JSON.parse(storedLoginInformation)
    } catch (error) {
        Sentry.setUser(null)
        if (lastReportedInvalidLoginInformation !== storedLoginInformation) {
            lastReportedInvalidLoginInformation = storedLoginInformation
            reportHandledError(error, {
                module: 'auth',
                operation: 'read_login_information'
            })
        }
        return
    }
    lastReportedInvalidLoginInformation = null

    const loginInformation = loginResponse.objectData
    if (loginInformation == null || typeof loginInformation !== 'object') {
        Sentry.setUser(null)
        return
    }

    const email = loginInformation.email
    Sentry.setUser(email == null || email === '' ? null : {email: String(email)})
}

export function clearSentryUser() {
    if (isSentryEnabled()) {
        lastSyncedLoginInformation = undefined
        lastReportedInvalidLoginInformation = null
        Sentry.setUser(null)
    }
}
